import { cn } from "@tcg/ui";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";

import { MoneyText } from "@/components/MoneyText";
import type { RefundProposal, ReturnCreate, ReturnPreview } from "@/lib/types";

export type RefundChoice = "original" | "cash" | "store_credit";

/** The ways back the screen offers — only those the server can do for this sale. */
export function refundChoices(preview: ReturnPreview, hasCustomer: boolean): RefundChoice[] {
  const cash = preview.capacity.find((c) => c.method === "cash")?.refundable ?? 0;
  const out: RefundChoice[] = ["original"];
  const onlyCash = preview.proposal.every((p) => p.method === "cash");
  if (!onlyCash && cash >= preview.total) {
    out.push("cash");
  }
  if (hasCustomer) {
    out.push("store_credit");
  }
  return out;
}

/** The `refunds[]` of `POST /pos/returns` — the amounts are the server's (preview), never computed. */
export function refundsOf(
  preview: ReturnPreview,
  choice: RefundChoice,
  edcRefs: Readonly<Record<number, string>>,
  customerId: string | null,
): ReturnCreate["refunds"] {
  if (choice === "cash") {
    return [{ method: "cash", amount: preview.total }];
  }
  if (choice === "store_credit") {
    return [
      { method: "store_credit", amount: preview.total, ...(customerId ? { customerId } : {}) },
    ];
  }
  return preview.proposal.map((p: RefundProposal, i) => ({
    method: p.method,
    amount: p.amount,
    ...(p.method === "edc_external" && edcRefs[i]?.trim()
      ? { externalRef: edcRefs[i]?.trim() }
      : {}),
  }));
}

const METHOD_KEY: Record<string, string> = {
  cash: "tender.method_cash",
  edc_external: "tender.method_card",
  store_credit: "tender.method_credit",
};

/**
 * How the money goes back (design/05 §3.6): by default the way it was paid (the server's split —
 * card terminal first, cash last); or all in cash; or all as store credit (valid up to 6 months,
 * 資金決済法). A card terminal refund is done on the terminal too — its reference is typed here.
 */
export function RefundMethods({
  preview,
  choice,
  onChoice,
  hasCustomer,
  edcRefs,
  onEdcRef,
}: {
  preview: ReturnPreview;
  choice: RefundChoice;
  onChoice: (choice: RefundChoice) => void;
  hasCustomer: boolean;
  edcRefs: Readonly<Record<number, string>>;
  onEdcRef: (index: number, value: string) => void;
}) {
  const { t } = useTranslation();
  const choices = refundChoices(preview, hasCustomer);
  return (
    <section aria-labelledby="refund-title" className="flex flex-col gap-3">
      <h2 id="refund-title" className="flex items-baseline justify-between text-lg font-semibold">
        {t("returns.refund_total")}
        <MoneyText amount={preview.total} size="xl" data-testid="refund-total" />
      </h2>
      <p className="text-sm text-muted-foreground">
        {t("returns.refund_tax")} <MoneyText amount={preview.tax} size="sm" />
      </p>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t("returns.refund_how")}>
        {choices.map((c) => (
          <button
            key={c}
            type="button"
            role="radio"
            aria-checked={choice === c}
            onClick={() => onChoice(c)}
            className="min-h-pos-touch rounded-md border px-4 text-lg aria-checked:border-primary aria-checked:bg-primary aria-checked:text-primary-foreground"
          >
            {t(`returns.refund_choice_${c}`)}
          </button>
        ))}
      </div>
      {choice === "original" ? (
        <ul className="flex flex-col gap-2" data-testid="refund-proposal">
          {preview.proposal.map((p, i) => (
            <li
              key={`${p.method}-${String(i)}`}
              className={cn(
                "flex flex-col gap-1 rounded-md border p-3",
                p.edcVoidRequired && "border-status-pending",
              )}
            >
              <span className="flex items-baseline justify-between text-lg">
                <span>
                  {t(METHOD_KEY[p.method] ?? "tender.method_card")}
                  {p.cardBrand
                    ? ` (${t(`brand.${p.cardBrand}`, { defaultValue: p.cardBrand })}${p.last4 ? ` ****${p.last4}` : ""})`
                    : ""}
                </span>
                <MoneyText amount={p.amount} size="lg" />
              </span>
              {p.method === "edc_external" ? (
                <>
                  <span className="flex items-center gap-2 text-sm">
                    <Info aria-hidden className="size-4 shrink-0" />
                    {t("returns.edc_hint")}
                  </span>
                  <label className="flex items-center gap-2">
                    <span className="text-sm">{t("returns.edc_ref")}</span>
                    <input
                      value={edcRefs[i] ?? ""}
                      onChange={(e) =>
                        onEdcRef(i, e.target.value.replace(/[^0-9A-Za-z-]/g, "").slice(0, 40))
                      }
                      inputMode="numeric"
                      autoComplete="off"
                      className="min-h-12 flex-1 rounded-md border px-3 font-mono text-lg"
                    />
                  </label>
                </>
              ) : null}
              {p.method === "store_credit" ? (
                <span className="text-sm text-muted-foreground">{t("returns.credit_expiry")}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="flex items-baseline justify-between rounded-md border p-3 text-lg">
          <span>{t(METHOD_KEY[choice] ?? "tender.method_cash")}</span>
          <MoneyText amount={preview.total} size="lg" />
        </p>
      )}
      {choice === "store_credit" ? (
        <p className="text-sm text-muted-foreground">{t("returns.credit_expiry")}</p>
      ) : null}
      {preview.henkanRequired ? <p className="text-sm">{t("returns.henkan_notice")}</p> : null}
    </section>
  );
}
